import { useEffect, useState } from "react";
import type { BoardBackground, BoardMeta, FrameElement, PageElement, PaperKey, PatternKey, SepiaElement } from "../../shared/model";
import type { Editor } from "../editor/editor";
import { useBoard, useEditor, useUi } from "../editor/react/hooks";
import { bounds } from "../editor/elements";
import { type Box, boxesIntersect, unionBoxes } from "../editor/geometry";
import { canvasToBlob, contentBox, releaseCanvas, renderToCanvas, renderToSvg, type RenderOptions } from "../editor/export";
import { makePdf } from "../editor/pdf";
import { PAPERS, PATTERNS } from "../editor/style";
import { stickerUrl } from "../data/stickers";
import { api } from "../sync/api";
import { navigate } from "../app/router";
import { ArrowDown, ArrowUp, Copy, Trash2 } from "lucide-react";
import { Button, Chip, Dialog, IconButton, Stepper, WavySlider, useToast } from "../ui/kit";

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** On iPad, the share sheet is the friendliest way to save a file (Photos, Files, AirDrop). */
async function saveOrShare(blob: Blob, name: string): Promise<void> {
  const file = new File([blob], name, { type: blob.type });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  const touch = matchMedia("(pointer: coarse)").matches;
  if (touch && nav.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: name });
      return;
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
    }
  }
  download(blob, name);
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "board";

type Scope = "all" | "page" | "selection";

/** One picture to export: a region of the board and what to draw in it. */
interface Sheet {
  box: Box;
  padding: number;
  els: SepiaElement[];
  pages?: PageElement[];
}

/**
 * What an export covers: a notebook's pages (or one), the whole board (or one PDF page per frame, when asked for),
 * or the selection. Everything on every visible layer is included.
 */
function sheetsFor(ed: Editor, scope: Scope, perPage: boolean): Sheet[] {
  const shown = ed.doc.all().filter((e) => ed.isShown(e));
  const on = (box: Box) => shown.filter((e) => boxesIntersect(bounds(e), box));
  const pages = ed.pages();
  if (scope === "selection" && ed.ui.selection.length) {
    const sel = ed.selected();
    return [{ box: contentBox(sel)!, padding: 40, els: sel, pages: pages.length ? pages : undefined }];
  }
  if (pages.length) {
    const chosen = scope === "page" ? [ed.currentPage()!] : pages;
    if (perPage || chosen.length === 1) return chosen.map((pg) => ({ box: pg, padding: 0, els: on(pg), pages: [pg] }));
    const all = unionBoxes(chosen)!;
    return [{ box: all, padding: 30, els: on(all), pages: chosen }];
  }
  const frames = shown.filter((e): e is FrameElement => e.type === "frame").sort((a, b) => a.y - b.y || a.x - b.x);
  if (perPage && frames.length) {
    // each frame's area, with whatever overlaps it (cut off at the frame's edge)
    return frames.map((f) => {
      const box = { x: f.x, y: f.y, w: f.w, h: f.h };
      return { box, padding: 0, els: on(box).filter((e) => e.type !== "frame") };
    });
  }
  return [{ box: contentBox(shown) ?? { x: 0, y: 0, w: 800, h: 600 }, padding: 40, els: shown }];
}

export function ExportDialog({ open, onClose, title, bg }: { open: boolean; onClose: () => void; title: string; bg: BoardBackground }) {
  const ed = useEditor();
  const toast = useToast();
  const [scope, setScope] = useState<Scope>("all");
  const [grid, setGrid] = useState(true);
  const [transparent, setTransparent] = useState(false);
  const [scale, setScale] = useState(2);
  const [perFrame, setPerFrame] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const hasSel = useUi((ui) => ui.selection.length > 0);
  const paged = useBoard((_, e) => e.isPaged());
  const hasFrames = useBoard((_, e) => !e.isPaged() && e.doc.all().some((el) => el.type === "frame" && e.isShown(el)));
  // start from everything (a selection left over from moving things between layers shouldn't shrink the export)
  useEffect(() => {
    if (open) setScope(paged ? "page" : "all");
  }, [open, paged]);
  const opts = (s: Sheet): RenderOptions => ({
    bg, scale, grid, transparent, box: s.box, padding: s.padding, pages: s.pages,
    resolveImage: (src: string) => ed.host.resolveImage(src), stickerUrl, layers: ed.layers(), layerOf: (e: SepiaElement) => ed.layerOf(e)
  });
  const one = () => sheetsFor(ed, scope, false)[0];
  const png = async () => {
    const s = one();
    const canvas = await renderToCanvas(s.els, opts(s));
    try {
      return await canvasToBlob(canvas);
    } finally {
      releaseCanvas(canvas);
    }
  };
  const pdf = async () => {
    const sheets = sheetsFor(ed, scope, paged || perFrame);
    // pages are drawn one at a time; each is freed once it is in the PDF
    return makePdf(sheets.length, async (i) => {
      const s = sheets[i];
      return { canvas: await renderToCanvas(s.els, opts(s)), width: s.box.w + s.padding * 2, height: s.box.h + s.padding * 2, transparent };
    }, title);
  };
  const run = async (kind: string, fn: () => Promise<void>) => {
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      toast((e as Error).message || "Export failed", "error");
    } finally {
      setBusy(null);
    }
  };
  const empty = !ed.doc.size;
  return (
    <Dialog open={open} onClose={onClose} title="Export">
      {empty ? (
        <p className="muted">This board is empty — draw something first.</p>
      ) : (
        <>
          <div className="form-row">
            <span className="form-label">what</span>
            <div className="chips">
              {paged && <Chip active={scope === "page"} onClick={() => setScope("page")}>this page</Chip>}
              <Chip active={scope === "all"} onClick={() => setScope("all")}>{paged ? "all pages" : "whole board"}</Chip>
              <Chip active={scope === "selection"} onClick={() => hasSel && setScope("selection")}>{hasSel ? "selection" : "selection (none)"}</Chip>
            </div>
          </div>
          <div className="form-row">
            <span className="form-label">paper</span>
            <div className="chips">
              <Chip active={!transparent && grid} onClick={() => { setTransparent(false); setGrid(true); }}>with grid</Chip>
              <Chip active={!transparent && !grid} onClick={() => { setTransparent(false); setGrid(false); }}>plain paper</Chip>
              <Chip active={transparent} onClick={() => setTransparent(true)}>transparent</Chip>
            </div>
          </div>
          {hasFrames && scope === "all" && (
            <div className="form-row">
              <span className="form-label">pdf</span>
              <div className="chips">
                <Chip active={!perFrame} onClick={() => setPerFrame(false)}>one page</Chip>
                <Chip active={perFrame} onClick={() => setPerFrame(true)}>a page per frame</Chip>
              </div>
            </div>
          )}
          <div className="form-row">
            <span className="form-label">sharpness</span>
            <div className="chips">
              {[1, 2, 3].map((s) => (
                <Chip key={s} active={scale === s} onClick={() => setScale(s)}>{s}×</Chip>
              ))}
            </div>
          </div>
          <div className="export-actions">
            <Button kind="primary" disabled={!!busy} onClick={() => run("png", async () => saveOrShare(await png(), slug(title) + ".png"))}>
              {busy === "png" ? "Drawing…" : "PNG image"}
            </Button>
            <Button disabled={!!busy} onClick={() => run("svg", async () => saveOrShare(new Blob([await renderToSvg(one().els, opts(one()))], { type: "image/svg+xml" }), slug(title) + ".svg"))}>
              {busy === "svg" ? "Drawing…" : "SVG vector"}
            </Button>
            <Button disabled={!!busy} onClick={() => run("pdf", async () => saveOrShare(await pdf(), slug(title) + ".pdf"))}>
              {busy === "pdf" ? "Making…" : "PDF"}
            </Button>
            <Button disabled={!!busy} onClick={() => run("json", async () => download(new Blob([JSON.stringify({ sepia: 1, title, bg, els: ed.doc.everything() })], { type: "application/json" }), slug(title) + ".sepia.json"))}>
              Sepia file
            </Button>
          </div>
          <p className="muted small">{paged ? "The PDF has one page for each page of the notebook." : "Exports include every visible layer."} Transparent leaves out the paper in every format. A Sepia file can be imported back later.</p>
        </>
      )}
    </Dialog>
  );
}

export function BackgroundDialog({ open, onClose, bg, onChange }: { open: boolean; onClose: () => void; bg: BoardBackground; onChange: (bg: BoardBackground) => void }) {
  const paged = useBoard((_, e) => e.isPaged());
  return (
    <Dialog open={open} onClose={onClose} title={paged ? "Paper & ruling" : "Paper & grid"} tape="var(--sage)">
      <div className="form-row">
        <span className="form-label">paper</span>
        <div className="paper-swatches">
          {(Object.keys(PAPERS) as PaperKey[]).map((k) => (
            <button key={k} type="button" className={"paper-swatch" + (bg.paper === k ? " active" : "")} style={{ background: PAPERS[k].color, color: PAPERS[k].ink }} onClick={() => onChange({ ...bg, paper: k })}>
              {PAPERS[k].label}
            </button>
          ))}
        </div>
      </div>
      <div className="form-row">
        <span className="form-label">pattern</span>
        <div className="chips">
          {PATTERNS.map((p) => (
            <Chip key={p.key} active={bg.pattern === p.key} onClick={() => onChange({ ...bg, pattern: p.key as PatternKey })}>
              {p.label}
            </Chip>
          ))}
        </div>
      </div>
      <div className="form-row">
        <span className="form-label">spacing</span>
        <Stepper value={bg.size} min={8} max={96} step={4} label="grid spacing" onChange={(v) => onChange({ ...bg, size: v })} />
      </div>
    </Dialog>
  );
}

export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ed = useEditor();
  const s = useUi((_, e) => e.settings);
  const keyboard = useUi((_, e) => e.keyboardMode);
  const set = (p: Partial<typeof s>) => ed.setSettings(p);
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(0);
  useEffect(() => {
    if (open) setSaved(ed.inputLog.reports.length);
  }, [open, ed]);
  const sendReport = async () => {
    setSending(true);
    try {
      const bundle = ed.inputLog.bundle();
      const build = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]')?.src.split("/").pop() ?? "dev";
      const { id } = await api.sendTouchReport({
        build, ua: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints, dpr: window.devicePixelRatio,
        screen: { w: window.innerWidth, h: window.innerHeight }, settings: ed.settings, camera: { z: ed.camera.z }, ...bundle
      });
      ed.inputLog.clear();
      setSaved(0);
      toast(`Touch report sent (${id.slice(-6)}). Thank you!`);
    } catch (err) {
      toast((err as Error).message || "Couldn't send the report", "error");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title="Pencil & gestures" tape="var(--denim)">
      <div className="form-row">
        <span className="form-label">write with</span>
        <div className="chips">
          <Chip active={!keyboard} onClick={() => ed.setWriteWith("pen")}>pen</Chip>
          <Chip active={keyboard} onClick={() => ed.setWriteWith("keyboard")}>keyboard</Chip>
        </div>
      </div>
      <p className="muted small">Keyboard: tap anywhere to type there; fingers move and zoom the page.</p>
      <div className="form-row">
        <span className="form-label">drawing</span>
        <div className="chips">
          <Chip active={s.penMode === "auto"} onClick={() => set({ penMode: "auto" })}>auto</Chip>
          <Chip active={s.penMode === "on"} onClick={() => set({ penMode: "on" })}>Pencil only</Chip>
          <Chip active={s.penMode === "off"} onClick={() => set({ penMode: "off" })}>fingers draw too</Chip>
        </div>
      </div>
      <p className="muted small">Auto: once an Apple Pencil touches the page, only the Pencil draws and fingers pan, pinch and tap.</p>
      <div className="form-row">
        <span className="form-label">pressure</span>
        <WavySlider value={2 - s.pressureGamma} min={0.5} max={1.5} onChange={(v) => set({ pressureGamma: Math.round((2 - v) * 100) / 100 })} label="Pressure sensitivity" left="firm" right="light" />
      </div>
      <label className="check-row">
        <input type="checkbox" checked={s.snapShapes} onChange={(e) => set({ snapShapes: e.target.checked })} />
        <span>Hold at the end of a stroke to snap it into a clean line or shape</span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={s.scribbleErase} onChange={(e) => set({ scribbleErase: e.target.checked })} />
        <span>Scribble over ink to erase it</span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={s.fingerUndo} onChange={(e) => set({ fingerUndo: e.target.checked })} />
        <span>Two-finger tap undoes, three-finger tap redoes</span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={s.leftHanded} onChange={(e) => set({ leftHanded: e.target.checked })} />
        <span>Left-handed: tools on the right</span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={s.toolLock} onChange={(e) => set({ toolLock: e.target.checked })} />
        <span>Keep shape and note tools selected after use</span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={s.snapToGrid} onChange={(e) => set({ snapToGrid: e.target.checked })} />
        <span>Snap moved things to the grid</span>
      </label>
      <div className="form-row">
        <span className="form-label">report</span>
        <Button onClick={sendReport} disabled={sending}>{sending ? "Sending…" : "Send a touch report"}</Button>
      </div>
      <p className="muted small">
        If the page zooms or moves when it shouldn't (say, as your hand rests to write), send this right after it
        happens: it holds only the last few seconds of touches{saved ? `, plus ${saved} saved moment${saved > 1 ? "s" : ""} when the zoom jumped` : ""}.
      </p>
    </Dialog>
  );
}

const SHORTCUTS: [string, string][] = [
  ["V · L · H", "select · lasso · hand"],
  ["P · M · E", "pen · highlighter · eraser"],
  ["R · O · D", "rectangle · ellipse · diamond"],
  ["A", "arrow & connector"],
  ["T · N · C", "text · sticky note · index card"],
  ["F · S · K", "frame · stickers · laser"],
  ["I", "insert image"],
  ["1 – 9", "ink colours"],
  ["⌘Z · ⇧⌘Z", "undo · redo"],
  ["⌘C · ⌘X · ⌘V · ⌘D", "copy · cut · paste · duplicate"],
  ["⌘G · ⇧⌘G", "group · ungroup"],
  ["⌘] · ⌘[", "bring forward · send backward"],
  ["⇧1 · ⇧2 · ⌘0", "fit all · zoom to selection · 100%"],
  ["space + drag", "pan"],
  ["Enter · Esc", "edit selection · done"],
  ["two / three-finger tap", "undo / redo (iPad)"],
  ["hold still at stroke end", "snap to shape"],
  ["scribble over ink", "erase it"]
];

export function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="Shortcuts & gestures" wide tape="var(--rose)">
      <div className="shortcuts">
        {SHORTCUTS.map(([k, v]) => (
          <div key={k} className="shortcut">
            <kbd>{k}</kbd>
            <span>{v}</span>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

export function ShareDialog({ open, onClose, board, onChange }: { open: boolean; onClose: () => void; board: BoardMeta; onChange: (b: Partial<BoardMeta>) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const link = board.shareToken && board.shareMode ? `${location.origin}/s/${board.shareToken}` : null;
  const setMode = async (mode: "view" | "edit" | null) => {
    setBusy(true);
    try {
      const r = await api.share(board.id, mode);
      onChange({ shareMode: r.shareMode, shareToken: r.shareToken ?? board.shareToken });
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title="Share this board" tape="var(--sage)">
      <div className="form-row">
        <span className="form-label">link</span>
        <div className="chips">
          <Chip active={!board.shareMode} onClick={() => !busy && setMode(null)}>private</Chip>
          <Chip active={board.shareMode === "view"} onClick={() => !busy && setMode("view")}>anyone can view</Chip>
          <Chip active={board.shareMode === "edit"} onClick={() => !busy && setMode("edit")}>signed-in people can edit</Chip>
        </div>
      </div>
      {link ? (
        <div className="share-link">
          <input readOnly value={link} onFocus={(e) => e.target.select()} />
          <Button
            kind="primary"
            onClick={async () => {
              try {
                if (navigator.share && matchMedia("(pointer: coarse)").matches) await navigator.share({ url: link, title: board.title });
                else {
                  await navigator.clipboard.writeText(link);
                  toast("Link copied");
                }
              } catch {
                /* cancelled */
              }
            }}
          >
            Copy link
          </Button>
        </div>
      ) : (
        <p className="muted">Only you can see this board. Turn on a link to share it.</p>
      )}
      <p className="muted small">Viewers see changes live, and your laser pointer too. Turning the link off stops access immediately.</p>
    </Dialog>
  );
}

function BoardLinks({ boardId }: { boardId: string }) {
  const [data, setData] = useState<{ out: { id: string; title: string }[]; back: { id: string; title: string }[] } | null>(null);
  useEffect(() => {
    let live = true;
    api
      .graph()
      .then((g) => {
        if (!live) return;
        const title = new Map(g.nodes.map((n) => [n.id, n.title]));
        const out = g.links.filter(([a]) => a === boardId).map(([, b]) => ({ id: b, title: title.get(b) ?? "?" }));
        const back = g.links.filter(([, b]) => b === boardId).map(([a]) => ({ id: a, title: title.get(a) ?? "?" }));
        setData({ out, back });
      })
      .catch(() => live && setData({ out: [], back: [] }));
    return () => {
      live = false;
    };
  }, [boardId]);
  if (!data) return <p className="muted small">Looking for links…</p>;
  const list = (items: { id: string; title: string }[], empty: string) =>
    items.length ? (
      <div className="chips">
        {items.map((b) => (
          <a key={b.id} className="chip link-chip" href={"/b/" + b.id} onClick={(e) => { e.preventDefault(); navigate("/b/" + b.id); }}>
            {b.title}
          </a>
        ))}
      </div>
    ) : (
      <p className="muted small">{empty}</p>
    );
  return (
    <div className="board-links">
      <div className="form-row">
        <span className="form-label">links to</span>
        {list(data.out, "No [[links]] on this board yet.")}
      </div>
      <div className="form-row">
        <span className="form-label">linked from</span>
        {list(data.back, "No other board links here yet.")}
      </div>
    </div>
  );
}

/** A small picture of one page of a notebook, redrawn a moment after edits settle. */
function PageThumb({ page }: { page: PageElement }) {
  const ed = useEditor();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let timer = 0;
    let live = true;
    const draw = () => {
      const pg = ed.doc.get(page.id);
      if (pg?.type !== "page") return;
      const els = ed.doc.all().filter((e) => ed.isShown(e) && boxesIntersect(bounds(e), pg));
      renderToCanvas(els, {
        bg: ed.bg, box: pg, padding: 0, scale: 150 / pg.w, grid: true, pages: [pg], layers: ed.layers(), layerOf: (e) => ed.layerOf(e),
        resolveImage: (s) => ed.host.resolveImage(s), stickerUrl, maxPixels: 150 * 220 * 1.5
      })
        .then((c) => {
          if (live) setSrc(c.toDataURL("image/jpeg", 0.8));
          releaseCanvas(c);
        })
        .catch(() => undefined);
    };
    draw();
    const off = ed.doc.subscribe(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(draw, 800);
    });
    return () => {
      live = false;
      off();
      window.clearTimeout(timer);
    };
  }, [ed, page.id]);
  return (
    <span className="nb-thumb-img" style={{ aspectRatio: `${page.w} / ${page.h}` }}>
      {src && <img src={src} alt="" draggable={false} />}
    </span>
  );
}

const PAGE_RULINGS: { key: PatternKey | ""; label: string }[] = [
  { key: "", label: "like the board" },
  { key: "lines", label: "ruled" },
  { key: "grid", label: "quadrille" },
  { key: "dots", label: "dots" },
  { key: "blank", label: "plain" },
  { key: "iso", label: "isometric" }
];

/** The pages of a notebook: jump to one, reorder, copy, remove, change a page's ruling, add more. */
function NotebookPages({ onClose }: { onClose: () => void }) {
  const ed = useEditor();
  const pages = useBoard((_, e) => e.pages());
  const readOnly = useUi((ui) => ui.readOnly);
  const [current] = useState(() => ed.currentPage()?.id);
  return (
    <div className="nb-pages">
      {pages.map((pg, i) => (
        <div key={pg.id} className={"nb-page" + (pg.id === current ? " current" : "")}>
          <button type="button" className="nb-thumb" onClick={() => { onClose(); ed.goToPage(pg.id); }} aria-label={`Go to page ${i + 1}`}>
            <PageThumb page={pg} />
          </button>
          <div className="nb-row">
            <span className="page-num">{i + 1}</span>
            {!readOnly && (
              <span className="nb-tools">
                <IconButton icon={ArrowUp} size={16} label="Move page up" tipSide="top" disabled={i === 0} onClick={() => ed.movePage(pg.id, i - 1)} />
                <IconButton icon={ArrowDown} size={16} label="Move page down" tipSide="top" disabled={i === pages.length - 1} onClick={() => ed.movePage(pg.id, i + 1)} />
                <IconButton icon={Copy} size={16} label="Duplicate page" tipSide="top" onClick={() => ed.duplicatePage(pg.id)} />
                <IconButton
                  icon={Trash2}
                  size={16}
                  label="Delete page"
                  tipSide="top"
                  className="danger"
                  disabled={pages.length < 2}
                  onClick={() => {
                    if (ed.pageContents(pg).length && !window.confirm(`Delete page ${i + 1} and everything on it?`)) return;
                    ed.deletePage(pg.id);
                  }}
                />
              </span>
            )}
          </div>
          {!readOnly && (
            <select className="nb-ruling" value={pg.pattern ?? ""} aria-label={`Ruling of page ${i + 1}`} onChange={(e) => ed.setPagePattern(pg.id, (e.target.value || null) as PatternKey | null)}>
              {PAGE_RULINGS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          )}
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          className="nb-add"
          onClick={() => {
            const id = ed.addPage(current);
            onClose();
            if (id) ed.fitPageWidth(id, true);
          }}
        >
          ＋ Add page
        </button>
      )}
    </div>
  );
}

export function PagesDialog({ open, onClose, boardId }: { open: boolean; onClose: () => void; boardId?: string }) {
  const ed = useEditor();
  const paged = useBoard((_, e) => e.isPaged());
  const frames = open && !paged ? (ed.doc.all().filter((e) => e.type === "frame" && ed.isShown(e)) as FrameElement[]).sort((a, b) => a.y - b.y || a.x - b.x) : [];
  if (paged) {
    return (
      <Dialog open={open} onClose={onClose} title="Pages & links" wide tape="var(--caramel)">
        {open && <NotebookPages onClose={onClose} />}
        {boardId && open && <BoardLinks boardId={boardId} />}
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onClose={onClose} title="Pages & links" tape="var(--caramel)">
      {frames.length ? (
        <div className="pages-list">
          {frames.map((f, i) => (
            <button
              key={f.id}
              type="button"
              className="page-item"
              onClick={() => {
                onClose();
                ed.zoomToBox({ x: f.x, y: f.y - 40, w: f.w, h: f.h + 40 }, 4);
                ed.select([f.id]);
              }}
            >
              <span className="page-num">{i + 1}</span>
              <span>{f.title || "Untitled"}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="muted">No pages yet. Draw a frame (F) around an area to make it a page — pages are your slides in Present mode.</p>
      )}
      <div className="export-actions">
        <Button onClick={() => { onClose(); ed.zoomToFit(); }}>Show everything</Button>
        {contentBox(ed.doc.all()) && <Button onClick={() => { onClose(); ed.setTool("frame"); }}>Draw a new page</Button>}
      </div>
      {boardId && open && <BoardLinks boardId={boardId} />}
    </Dialog>
  );
}
